import { useContext, useEffect, useRef, useState, useCallback } from 'react'
import {
  Stack,
  Text,
  DetailsList,
  IColumn,
  SelectionMode,
  DetailsListLayoutMode,
  Modal,
  IconButton,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Label,
  ComboBox,
  IComboBoxOption,
  TooltipHost,
  DirectionalHint,
  DefaultButton,
} from '@fluentui/react'
import { AppStateContext } from '../../state/AppProvider'

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { DashboardFilters, NegativeFeedbackItem } from '../../api/models'
import { getFeedbackDetails, getFilterUsers } from '../../api/api'
import ChartFromJson, { isChartJson } from '../../components/charts/ChartFromJson'
import DateRangeFilter from '../../components/filters/DateRangeFilter'
import UserFilter from '../../components/filters/UserFilter'
import LoadingSpinner from '../../components/common/LoadingSpinner'

const cardStyle = {
  root: {
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
}

const CopyableTextBlock = ({ label, text }: { label: string; text: string }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Stack tokens={{ childrenGap: 4 }}>
      <Label>{label}</Label>
      <div
        className="markdown-content"
        style={{
          padding: '10px 12px',
          backgroundColor: '#f3f2f1',
          borderRadius: 4,
          border: '1px solid #edebe9',
          fontSize: 13,
          lineHeight: '1.6',
          color: '#323130',
          userSelect: 'text',
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {text}
        </ReactMarkdown>
      </div>
      <DefaultButton
        iconProps={{ iconName: copied ? 'CheckMark' : 'Copy' }}
        text={copied ? 'Copied!' : 'Copy'}
        onClick={handleCopy}
        styles={{
          root: {
            minWidth: 90,
            height: 30,
            padding: '0 10px',
            alignSelf: 'flex-end',
            borderColor: copied ? '#5B8A72' : '#8a8886',
            color: copied ? '#5B8A72' : '#323130',
          },
          rootHovered: {
            borderColor: '#2B3A67',
            color: '#2B3A67',
          },
          icon: {
            fontSize: 14,
            color: copied ? '#5B8A72' : '#605e5c',
          },
          label: {
            fontSize: 12,
            fontWeight: 400,
          },
        }}
      />
    </Stack>
  )
}

const feedbackTypeOptions: IComboBoxOption[] = [
  { key: 'all', text: 'All Feedback' },
  { key: 'positive', text: 'Positive' },
  { key: 'negative', text: 'Negative' },
]

const Feedback = () => {
  const appStateContext = useContext(AppStateContext)
  const state = appStateContext?.state

  const feedbackMessageRef = useRef<HTMLDivElement | null>(null)

  const scrollToFeedbackMessage = () => {
    const target = feedbackMessageRef.current
    if (!target) return
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const PAGE_SIZE = 25

  const getDefaultFeedbackFilters = (): DashboardFilters => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 14)
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    }
  }

  const [filters, setFilters] = useState<DashboardFilters>(state?.feedbackFilters || getDefaultFeedbackFilters())
  const [feedbackType, setFeedbackType] = useState<string | undefined>(state?.feedbackType)
  const [items, setItems] = useState<NegativeFeedbackItem[]>(state?.feedbackData || [])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedItem, setSelectedItem] = useState<NegativeFeedbackItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortColumn, setSortColumn] = useState<string>('created_at')
  const [sortDescending, setSortDescending] = useState(true)

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDescending(!sortDescending)
    } else {
      setSortColumn(column)
      setSortDescending(true)
    }
    setCurrentPage(1)
  }

  const sortedItems = [...items].sort((a, b) => {
    const key = sortColumn as keyof NegativeFeedbackItem
    const aVal = a[key]
    const bVal = b[key]
    // Handle arrays (citations) by comparing length
    if (Array.isArray(aVal) && Array.isArray(bVal)) {
      return sortDescending ? bVal.length - aVal.length : aVal.length - bVal.length
    }
    const aStr = String(aVal ?? '').toLowerCase()
    const bStr = String(bVal ?? '').toLowerCase()
    const cmp = aStr.localeCompare(bStr)
    return sortDescending ? -cmp : cmp
  })

  const dispatch = appStateContext?.dispatch

  const fetchData = useCallback(async (currentFilters: DashboardFilters, type?: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await getFeedbackDetails(currentFilters, type)
      setItems(data)
      setCurrentPage(1)
      if (dispatch) {
        dispatch({ type: 'SET_FEEDBACK_DATA', payload: { items: data, filters: currentFilters, feedbackType: type } })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback')
    } finally {
      setIsLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (!state?.feedbackData) {
      fetchData(filters, feedbackType)
    }
    // Load user filter options if not cached
    if (dispatch && (!state?.users || state.users.length === 0)) {
      getFilterUsers().then((users) => dispatch({ type: 'SET_USERS', payload: users })).catch(() => {})
    }
  }, [])

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return iso
    }
  }

  const truncate = (text: string, max: number) => {
    if (!text) return '-'
    return text.length > max ? text.substring(0, max) + '...' : text
  }

  const feedbackLabel = (value: string) => {
    if (value === 'positive') return 'Positive'
    if (value === 'negative') return 'Negative'
    return value
  }

  const feedbackColor = (value: string) => {
    if (value === 'positive') return '#5B8A72'
    if (value === 'negative') return '#D4775B'
    return '#605e5c'
  }

  const columns: IColumn[] = [
    {
      key: 'created_at',
      name: 'Date',
      fieldName: 'created_at',
      minWidth: 140,
      maxWidth: 170,
      isSorted: sortColumn === 'created_at',
      isSortedDescending: sortColumn === 'created_at' && sortDescending,
      iconName: sortColumn !== 'created_at' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('created_at'),
      onRender: (item: NegativeFeedbackItem) => formatDate(item.created_at),
    },
    {
      key: 'user_name',
      name: 'User',
      fieldName: 'user_name',
      minWidth: 120,
      maxWidth: 180,
      isSorted: sortColumn === 'user_name',
      isSortedDescending: sortColumn === 'user_name' && sortDescending,
      iconName: sortColumn !== 'user_name' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('user_name'),
    },
    {
      key: 'feedback',
      name: 'Type',
      fieldName: 'feedback',
      minWidth: 60,
      maxWidth: 75,
      isSorted: sortColumn === 'feedback',
      isSortedDescending: sortColumn === 'feedback' && sortDescending,
      iconName: sortColumn !== 'feedback' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('feedback'),
      onRender: (item: NegativeFeedbackItem) => (
        <Text styles={{ root: { color: feedbackColor(item.feedback), fontWeight: 600 } }}>
          {feedbackLabel(item.feedback)}
        </Text>
      ),
    },
    {
      key: 'documents',
      name: 'Documents',
      fieldName: 'documents',
      minWidth: 150,
      maxWidth: 250,
      isMultiline: true,
      onRender: (item: NegativeFeedbackItem) => {
        if (!item.documents || item.documents.length === 0) return '-'
        return item.documents.map((d) => d.title || 'Untitled').join(', ')
      },
    },
    {
      key: 'user_question',
      name: 'User Question',
      fieldName: 'user_question',
      minWidth: 200,
      isMultiline: true,
      isSorted: sortColumn === 'user_question',
      isSortedDescending: sortColumn === 'user_question' && sortDescending,
      iconName: sortColumn !== 'user_question' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('user_question'),
      onRender: (item: NegativeFeedbackItem) => {
        const text = item.user_question || '-'
        if (text.length <= 150) return text
        return (
          <TooltipHost
            content={text}
            directionalHint={DirectionalHint.bottomLeftEdge}
            styles={{ root: { display: 'block' } }}
            calloutProps={{
              styles: {
                root: { maxWidth: 500 },
                beak: { display: 'none' },
                calloutMain: {
                  padding: '14px 18px',
                  backgroundColor: '#fff',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
                  borderRadius: 6,
                  fontSize: 13,
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                },
              },
            }}
          >
            {truncate(text, 150)}
          </TooltipHost>
        )
      },
    },
    {
      key: 'assistant_response',
      name: 'Assistant Response',
      fieldName: 'assistant_response',
      minWidth: 200,
      isMultiline: true,
      isSorted: sortColumn === 'assistant_response',
      isSortedDescending: sortColumn === 'assistant_response' && sortDescending,
      iconName: sortColumn !== 'assistant_response' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('assistant_response'),
      onRender: (item: NegativeFeedbackItem) => {
        const text = item.assistant_response || '-'
        if (text.length <= 150) return text
        return (
          <TooltipHost
            content={text}
            directionalHint={DirectionalHint.bottomLeftEdge}
            styles={{ root: { display: 'block' } }}
            calloutProps={{
              styles: {
                root: { maxWidth: 500 },
                beak: { display: 'none' },
                calloutMain: {
                  padding: '14px 18px',
                  backgroundColor: '#fff',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
                  borderRadius: 6,
                  fontSize: 13,
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                },
              },
            }}
          >
            {truncate(text, 150)}
          </TooltipHost>
        )
      },
    },
    {
      key: 'citations',
      name: 'Citations',
      fieldName: 'citations',
      minWidth: 200,
      isMultiline: true,
      isSorted: sortColumn === 'citations',
      isSortedDescending: sortColumn === 'citations' && sortDescending,
      iconName: sortColumn !== 'citations' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('citations'),
      onRender: (item: NegativeFeedbackItem) => {
        if (!item.citations || item.citations.length === 0) return '-'
        return item.citations
          .map((c, i) => `[${i + 1}] ${c.title || c.filepath || 'Untitled'}${c.page_number != null ? ` (p. ${c.page_number})` : ''}`)
          .join(', ')
      },
    },
    {
      key: 'comment',
      name: 'Comment',
      fieldName: 'comment',
      minWidth: 200,
      isSorted: sortColumn === 'comment',
      isSortedDescending: sortColumn === 'comment' && sortDescending,
      iconName: sortColumn !== 'comment' ? 'Sort' : undefined,
      onColumnClick: () => handleSort('comment'),
      isMultiline: true,
      onRender: (item: NegativeFeedbackItem) => item.comment || '-',
    },
  ]

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
        User Feedback
      </Text>

      {/* Filters */}
      <Stack
        horizontal
        verticalAlign="end"
        tokens={{ childrenGap: 16 }}
        styles={{ root: { ...cardStyle.root, flexWrap: 'wrap' } }}
      >
        <DateRangeFilter
          startDate={filters.startDate || ''}
          endDate={filters.endDate || ''}
          onStartDateChange={(date) => setFilters({ ...filters, startDate: date })}
          onEndDateChange={(date) => setFilters({ ...filters, endDate: date })}
        />
        <UserFilter
          users={state?.users || []}
          selectedUserId={filters.userId}
          onChange={(userId) => setFilters({ ...filters, userId })}
        />
        <Stack>
          <Label>Feedback Type</Label>
          <ComboBox
            selectedKey={feedbackType ?? 'all'}
            options={feedbackTypeOptions}
            onChange={(_, option) => {
              if (option) {
                setFeedbackType(option.key === 'all' ? undefined : (option.key as string))
              }
            }}
            styles={{ root: { width: 160 } }}
          />
        </Stack>
        <PrimaryButton
          text="Apply"
          onClick={() => fetchData(filters, feedbackType)}
          disabled={isLoading}
        />
      </Stack>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {isLoading && <LoadingSpinner label="Loading feedback..." />}

      {!isLoading && (() => {
        const totalPages = Math.ceil(sortedItems.length / PAGE_SIZE)
        const startIdx = (currentPage - 1) * PAGE_SIZE
        const pagedItems = sortedItems.slice(startIdx, startIdx + PAGE_SIZE)

        return (
          <Stack styles={cardStyle} tokens={{ childrenGap: 12 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: '#605e5c' } }}>
                Feedback Items
              </Text>
              <Text variant="small" styles={{ root: { color: '#a19f9d' } }}>
                {items.length > PAGE_SIZE
                  ? `Showing ${startIdx + 1}-${Math.min(startIdx + PAGE_SIZE, items.length)} of ${items.length} items`
                  : `${items.length} item${items.length !== 1 ? 's' : ''}`}
              </Text>
            </Stack>
            {items.length > 0 ? (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <DetailsList
                    items={pagedItems}
                    columns={columns}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                    isHeaderVisible={true}
                    onRenderRow={(props, defaultRender) =>
                      props && defaultRender ? (
                        <div
                          onClick={() => setSelectedItem(props.item as NegativeFeedbackItem)}
                          style={{ cursor: 'pointer' }}
                        >
                          {defaultRender(props)}
                        </div>
                      ) : null
                    }
                    onShouldVirtualize={() => false}
                  />
                </div>
                <Stack
                  horizontal
                  horizontalAlign="center"
                  verticalAlign="center"
                  tokens={{ childrenGap: 8 }}
                  styles={{ root: { paddingTop: 12 } }}
                >
                  <IconButton
                    iconProps={{ iconName: 'ChevronLeft' }}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    ariaLabel="Previous page"
                  />
                  <Text variant="medium">
                    Page {currentPage} of {totalPages}
                  </Text>
                  <IconButton
                    iconProps={{ iconName: 'ChevronRight' }}
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    ariaLabel="Next page"
                  />
                </Stack>
              </>
            ) : (
              <Text styles={{ root: { color: '#a19f9d', textAlign: 'center', padding: 40 } }}>
                No feedback found for this period.
              </Text>
            )}
          </Stack>
        )
      })()}

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedItem}
        onDismiss={() => setSelectedItem(null)}
        isBlocking={false}
        styles={{
          main: {
            maxWidth: 720,
            width: '90vw',
            maxHeight: '85vh',
            borderRadius: 8,
            overflow: 'hidden',
          },
          scrollableContent: {
            padding: 24,
            overflowY: 'auto',
            maxHeight: '85vh',
          },
        }}
      >
        {selectedItem && (
          <Stack tokens={{ childrenGap: 16 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
                Feedback Detail
              </Text>
              <IconButton
                iconProps={{ iconName: 'Cancel' }}
                ariaLabel="Close"
                onClick={() => setSelectedItem(null)}
              />
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 24 }}>
              <Stack tokens={{ childrenGap: 4 }}>
                <Label>User</Label>
                <Text>{selectedItem.user_name} ({selectedItem.user_email})</Text>
              </Stack>
              <Stack tokens={{ childrenGap: 4 }}>
                <Label>Date</Label>
                <Text>{formatDate(selectedItem.created_at)}</Text>
              </Stack>
              <Stack tokens={{ childrenGap: 4 }}>
                <Label>Type</Label>
                <Text styles={{ root: { color: feedbackColor(selectedItem.feedback), fontWeight: 600 } }}>
                  {feedbackLabel(selectedItem.feedback)}
                </Text>
              </Stack>
              {selectedItem.plan_name && (
                <Stack tokens={{ childrenGap: 4 }}>
                  <Label>Plan</Label>
                  <Text>{selectedItem.plan_name}</Text>
                </Stack>
              )}
            </Stack>

            {selectedItem.documents && selectedItem.documents.length > 0 && (
              <Stack tokens={{ childrenGap: 6 }}>
                <Label>Documents Queried</Label>
                <Stack tokens={{ childrenGap: 4 }}>
                  {selectedItem.documents.map((doc, idx) => (
                    <Stack
                      key={idx}
                      horizontal
                      verticalAlign="center"
                      tokens={{ childrenGap: 8 }}
                      styles={{
                        root: {
                          padding: '6px 10px',
                          backgroundColor: '#f3f2f1',
                          borderRadius: 4,
                          borderLeft: '3px solid #2B3A67',
                        },
                      }}
                    >
                      <Text variant="small" styles={{ root: { color: '#323130' } }}>
                        {doc.title || 'Untitled'}
                        {doc.data_year ? ` (${doc.data_year})` : ''}
                      </Text>
                      {doc.url && (
                        <a
                          href={doc.url.startsWith('http') ? doc.url : `https://query.cembenchmarking.com${doc.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 11, color: '#2B3A67' }}
                        >
                          Open
                        </a>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}

            <Stack tokens={{ childrenGap: 8 }}>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Label>Conversation</Label>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                  {selectedItem.conversation && selectedItem.conversation.some((m) => m.id === selectedItem.id) && (
                    <DefaultButton
                      iconProps={{ iconName: 'DoubleChevronDown' }}
                      text={`Jump to ${feedbackLabel(selectedItem.feedback).toLowerCase()} feedback`}
                      onClick={scrollToFeedbackMessage}
                      styles={{
                        root: {
                          height: 28,
                          padding: '0 10px',
                          borderColor: feedbackColor(selectedItem.feedback),
                          color: feedbackColor(selectedItem.feedback),
                        },
                        rootHovered: {
                          borderColor: feedbackColor(selectedItem.feedback),
                          color: feedbackColor(selectedItem.feedback),
                          backgroundColor: '#f3f2f1',
                        },
                        icon: { fontSize: 12, color: feedbackColor(selectedItem.feedback) },
                        label: { fontSize: 12, fontWeight: 600 },
                      }}
                    />
                  )}
                  <Text variant="small" styles={{ root: { color: '#a19f9d' } }}>
                    {selectedItem.conversation?.length || 0} message{(selectedItem.conversation?.length || 0) !== 1 ? 's' : ''}
                  </Text>
                </Stack>
              </Stack>
              {selectedItem.conversation && selectedItem.conversation.length > 0 ? (
                <Stack tokens={{ childrenGap: 10 }}>
                  {selectedItem.conversation.map((msg, idx) => {
                    const isFeedbackMsg = msg.id === selectedItem.id
                    const isUser = msg.role === 'user'
                    const accent = isFeedbackMsg
                      ? feedbackColor(selectedItem.feedback)
                      : isUser
                        ? '#2B3A67'
                        : '#8a8886'
                    const bg = isFeedbackMsg
                      ? selectedItem.feedback === 'positive'
                        ? '#f0f7f2'
                        : '#fdf2ef'
                      : isUser
                        ? '#E8EDF4'
                        : '#f3f2f1'
                    return (
                      <div
                        key={msg.id || idx}
                        ref={isFeedbackMsg ? feedbackMessageRef : undefined}
                        style={{ scrollMarginTop: 12 }}
                      >
                      <Stack
                        tokens={{ childrenGap: 6 }}
                        styles={{
                          root: {
                            padding: '12px 16px',
                            borderRadius: 8,
                            backgroundColor: bg,
                            borderLeft: `3px solid ${accent}`,
                          },
                        }}
                      >
                        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                            <Text
                              variant="smallPlus"
                              styles={{
                                root: {
                                  fontWeight: 600,
                                  color: isUser ? '#2B3A67' : '#605e5c',
                                  textTransform: 'capitalize',
                                },
                              }}
                            >
                              {msg.role}
                            </Text>
                            {isFeedbackMsg && (
                              <Text
                                variant="tiny"
                                styles={{
                                  root: {
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    backgroundColor: accent,
                                    color: '#fff',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  },
                                }}
                              >
                                {feedbackLabel(selectedItem.feedback)} feedback
                              </Text>
                            )}
                          </Stack>
                          <Text variant="tiny" styles={{ root: { color: '#a19f9d' } }}>
                            {msg.created_at ? formatDate(msg.created_at) : ''}
                          </Text>
                        </Stack>

                        {isChartJson(msg.content) ? (
                          <ChartFromJson chartData={JSON.parse(msg.content.trim())} />
                        ) : (
                          <div className="markdown-content" style={{ fontSize: 13, lineHeight: '1.6', color: '#323130' }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                              {msg.content}
                            </ReactMarkdown>
                          </div>
                        )}

                        {isFeedbackMsg && selectedItem.comment && (
                          <Stack
                            tokens={{ childrenGap: 2 }}
                            styles={{
                              root: {
                                padding: '8px 10px',
                                backgroundColor: '#fff',
                                borderRadius: 4,
                                borderLeft: `3px solid ${accent}`,
                              },
                            }}
                          >
                            <Text variant="tiny" styles={{ root: { fontWeight: 600, color: '#605e5c', textTransform: 'uppercase', letterSpacing: '0.5px' } }}>
                              User Feedback
                            </Text>
                            <Text variant="small" styles={{ root: { fontStyle: 'italic', color: '#323130' } }}>
                              "{selectedItem.comment}"
                            </Text>
                          </Stack>
                        )}

                        {msg.citations && msg.citations.length > 0 && (
                          <Stack tokens={{ childrenGap: 4 }} styles={{ root: { marginTop: 4 } }}>
                            <Text variant="tiny" styles={{ root: { fontWeight: 600, color: '#605e5c', textTransform: 'uppercase', letterSpacing: '0.5px' } }}>
                              Sources ({msg.citations.length})
                            </Text>
                            {msg.citations.map((citation, cIdx) => (
                              <Stack
                                key={cIdx}
                                horizontal
                                verticalAlign="center"
                                tokens={{ childrenGap: 6 }}
                                styles={{
                                  root: {
                                    padding: '4px 8px',
                                    backgroundColor: '#edebe9',
                                    borderRadius: 4,
                                  },
                                }}
                              >
                                <Text variant="small" styles={{ root: { color: '#323130' } }}>
                                  [{cIdx + 1}] {citation.title || citation.filepath || 'Untitled'}{citation.page_number != null ? ` (p. ${citation.page_number})` : ''}
                                </Text>
                                {citation.url && (
                                  <a
                                    href={citation.url.startsWith('http') ? citation.url : `https://query.cembenchmarking.com${citation.url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: 11, color: '#2B3A67' }}
                                  >
                                    Open
                                  </a>
                                )}
                              </Stack>
                            ))}
                          </Stack>
                        )}
                      </Stack>
                      </div>
                    )
                  })}
                </Stack>
              ) : (
                <>
                  <CopyableTextBlock label="User Question" text={selectedItem.user_question || '(no question found)'} />
                  {isChartJson(selectedItem.assistant_response) ? (
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Label>Assistant Response (Chart)</Label>
                      <ChartFromJson chartData={JSON.parse(selectedItem.assistant_response.trim())} />
                    </Stack>
                  ) : (
                    <CopyableTextBlock label="Assistant Response" text={selectedItem.assistant_response} />
                  )}
                  {selectedItem.comment && (
                    <CopyableTextBlock label="User Feedback" text={selectedItem.comment} />
                  )}
                </>
              )}
            </Stack>
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}

export default Feedback
